import ApplicationComponent from "legacy/ApplicationComponent"
import FormGroup from "legacy/FormGroup"
import ContractGroupV2PanelPage from "legacy/contract-groups-v2/ContractGroupV2PanelPage"
import {
  centsToDollarsRaw,
  isPresent,
  safePercentageDecimal,
  snakeCaseToReadable,
} from "legacy/helpers.es6"
import { validatorFactory } from "legacy/validatorFactory"
import _ from "lodash"
import PropTypes from "prop-types"
import React from "react"

// Note: $context is loaded globally
// Note: Amplitude is loaded globally

class ContractGroupV2Rate extends ApplicationComponent {
  constructor(props) {
    super(props)
    this.validator = validatorFactory({ autoForceUpdate: this })
    this.state = {
      revenueCategoryId: this.props.form.revenueCategoryId || "",
      rate: {
        ...this.props.form.rate,
        longTermStorageProductId:
          props.form.rate.longTermStorageProductId ||
          ($context.mbmEnabled
            ? props.longTermStorageProducts[0].id
            : undefined),
      },
      selectedRateValue: isPresent(this.props.form.namedRateId)
        ? this.props.form.namedRateId
        : "custom",
      namedRateSelected: isPresent(this.props.form.namedRateId),
      rateVisited: this.props.form.rateVisited,
    }
    this.next = this.next.bind(this)
    this.handleRateSelection = this.handleRateSelection.bind(this)
  }

  fetchData() {
    return this.state
  }

  componentDidUpdate() {
    if (!this.state.error && this.props.incomplete && this.state.rateVisited) {
      this.setState({ error: true })
      this.validator.showMessages()
    }
  }

  componentWillUnmount() {
    Amplitude.trackEvent("contracts_v2:rate_complete")
    this.props.update({ ...this.state, rateVisited: true })
  }

  next() {
    this.props.route.goNext()
  }

  renderOptionGroup(optionGroupName, options) {
    return (
      <optgroup
        key={optionGroupName}
        label={snakeCaseToReadable(optionGroupName)}
      >
        {_.map(options, (option) => (
          <option key={option.value} value={option.value}>
            {snakeCaseToReadable(option.name)}
          </option>
        ))}
      </optgroup>
    )
  }

  renderRevenueCategoryChooser() {
    if ($context.revenueCategoriesEnabled) {
      return (
        <div className="row form-row">
          <div className="col-xs-12 col-sm-12">
            <FormGroup
              inputType="select"
              label="Revenue Category"
              name="revenueCategoryId"
              value={this.state.revenueCategoryId}
              onChange={this.handleInputChange}
            >
              <option key="blank" value="">
                Automatic
              </option>
              {_.map(this.props.revenueCategories, (category) => (
                <option key={category.id} value={category.id}>
                  {category.displayName}
                </option>
              ))}
            </FormGroup>
          </div>
        </div>
      )
    }
  }

  renderLongTermStorageProductChooser() {
    return (
      <div className="row form-row">
        <div className="col-xs-12 col-sm-12">
          <FormGroup
            inputType="select"
            label="Product Type"
            name="rate.longTermStorageProductId"
            value={this.state.rate.longTermStorageProductId}
            onChange={this.handleInputChange}
            required
            validation={this.validator.message(
              "productType",
              this.state.rate.longTermStorageProductId,
              "required"
            )}
          >
            {_.map(this.props.longTermStorageProducts, (product) => (
              <option key={product.id} value={product.id}>
                {product.name}
              </option>
            ))}
          </FormGroup>
        </div>
      </div>
    )
  }

  renderRateChooser() {
    return (
      <div className="row form-row">
        <div className="col-xs-12 col-sm-12">
          <FormGroup
            inputType="select"
            label="Rate"
            subLabel={
              <div>
                Visit the{" "}
                <a
                  target="_blank"
                  href={this.props.pricingUrl}
                  rel="noreferrer"
                >
                  Pricing
                </a>{" "}
                settings to create and update installment rates.
              </div>
            }
            name="rate"
            value={this.state.selectedRateValue}
            onChange={this.handleRateSelection}
          >
            <optgroup label="Installment Rate">
              {_.map(this.props.namedRates, (rate) => (
                <option key={rate.id} value={rate.id} data={rate}>
                  {rate.name}
                </option>
              ))}
            </optgroup>
            <optgroup label="Custom Rate">
              <option value="custom">Custom rate</option>
            </optgroup>
          </FormGroup>
          {this.renderCustomRateForm()}
        </div>
      </div>
    )
  }

  handleRateSelection(e) {
    let id = e.target.value
    let state = {}
    if (id === "custom") {
      state = {
        rate: {
          amount: "",
          taxRate: "",
          pricingStructure: "",
        },
        namedRateSelected: false,
        namedRateId: "",
      }
    } else {
      id = parseInt(id)
      const namedRate = this.props.namedRates.find((rate) => rate.id === id)
      state.rate = {
        pricingStructure: namedRate.pricingStructure,
        amount: centsToDollarsRaw(namedRate.amount),
        taxRate: safePercentageDecimal(namedRate.taxRate),
      }
      state.namedRateSelected = true
      state.namedRateId = id
      // full stay rates are not valid with M2M contracts
      if (
        state.rate.pricingStructure &&
        !state.rate.pricingStructure.includes("_month") &&
        this.props.form.contractType === "month_to_month"
      ) {
        this.props.form.contractType = "standard"
        this.props.form.endDate = null
      }
    }
    state.selectedRateValue = id
    this.setState(state)
    this.validator.hideMessages()
  }

  renderCustomRateForm() {
    return (
      <div
        className={`form-row ${
          this.state.namedRateSelected ? "text-muted" : ""
        }`}
      >
        <div className="col-xs-12 col-sm-6">
          <FormGroup
            disabled={this.state.namedRateSelected}
            name="rate.pricingStructure"
            label="Pricing Structure"
            inputType="select"
            value={this.state.rate.pricingStructure || ""}
            onChange={this.handleInputChange}
            required
            validation={this.validator.message(
              "pricingStructure",
              this.state.rate.pricingStructure,
              "required"
            )}
          >
            <option disabled value="">
              Select a pricing structure
            </option>
            {this.renderOptionGroup(
              "Monthly",
              this.props.pricingStructures.monthly
            )}
            {this.props.form.contractType === "standard" &&
              this.renderOptionGroup(
                "Full Stay",
                this.props.pricingStructures.full_stay
              )}
          </FormGroup>
        </div>
        <div className="col-xs-6 col-sm-3">
          <FormGroup
            disabled={this.state.namedRateSelected}
            inputType="currency"
            name="rate.amount"
            label="Amount"
            value={this.state.rate.amount || ""}
            onChange={this.handleInputChange}
            required
            validation={this.validator.message(
              "rate",
              this.state.rate.amount,
              "required"
            )}
          />
        </div>
        <div className="col-xs-6 col-sm-3">
          <FormGroup
            disabled={this.state.namedRateSelected}
            inputType="percentage"
            name="rate.taxRate"
            label="Tax"
            value={this.state.rate.taxRate || ""}
            onChange={this.handleInputChange}
          />
        </div>
      </div>
    )
  }

  render() {
    return (
      <ContractGroupV2PanelPage
        hasNext={this.props.route.hasNext}
        hasPrevious={this.props.route.hasPrevious}
        goNext={this.next}
        goPrevious={this.props.route.goPrevious}
        saving={this.props.saving}
      >
        {this.renderRateChooser()}
        {/* https://wanderlustgroup.atlassian.net/browse/TB-901 */}
        {$context.mbmEnabled
          ? this.renderLongTermStorageProductChooser()
          : this.renderRevenueCategoryChooser()}
      </ContractGroupV2PanelPage>
    )
  }
}

ContractGroupV2Rate.propTypes = {
  form: PropTypes.shape({
    revenueCategoryId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    rate: PropTypes.shape({
      longTermStorageProductId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      taxRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      pricingStructure: PropTypes.string,
    }),
    namedRateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    rateVisited: PropTypes.bool,
    contractType: PropTypes.string,
    endDate: PropTypes.string,
  }).isRequired,
  update: PropTypes.func.isRequired,
  route: PropTypes.shape({
    hasNext: PropTypes.bool,
    hasPrevious: PropTypes.bool,
    goNext: PropTypes.func,
    goPrevious: PropTypes.func,
  }).isRequired,
  pricingUrl: PropTypes.string,
  revenueCategories: PropTypes.array,
  longTermStorageProducts: PropTypes.array,
  namedRates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      pricingStructure: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
      taxRate: PropTypes.number,
    })
  ),
  pricingStructures: PropTypes.shape({
    monthly: PropTypes.array,
    full_stay: PropTypes.array,
  }),
  saving: PropTypes.bool,
  incomplete: PropTypes.bool,
}

export default ContractGroupV2Rate
